'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.0.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init Bartender after mobile menu is ready
		document.addEventListener('mobilemenu-init-done', () => {
			window.bartender = new Bartender({
				debug: false,
				trapFocus: true,
				overlay: true,
			});

			// Get elements in site search
			const siteSearchWrap = document.getElementById('siteSearch__wrap')
			const siteSearchClose = document.getElementById('siteSearch__close')
			const siteSearchQuery = document.getElementById('siteSearch__query')

			window.addEventListener('bartender-open', (e) => {
				// When top bar is opened, animate elements in and focus on query input
				if (e.detail.bar.position == 'top') {
					if (siteSearchWrap) siteSearchWrap.classList.add('siteSearch__wrap--visible')
					if (siteSearchClose) siteSearchClose.classList.add('siteSearch__close--visible')
					if (siteSearchQuery) siteSearchQuery.focus()
				}
			})

			window.addEventListener('bartender-close', (e) => {
				// When top bar is closed, animate elements out
				if (e.detail.bar.position == 'top') {
					if (siteSearchWrap) siteSearchWrap.classList.remove('siteSearch__wrap--visible')
					if (siteSearchClose) siteSearchClose.classList.remove('siteSearch__close--visible')
				}
			})
		});

		// Init mobile menu
		const mobileMenu = new MobileMenu();
		mobileMenu.init();

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initShowMoreButtons();
		this.initLiveSearch();
		this.initHelpNavLinks();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 *
	 * Find all links to images and use GLightbox to display them.
	 */
	initImageLinks() {
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);

		if (!parentNode) return;

		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			let figcaption = link.parentNode.querySelector('figcaption');
			let caption = figcaption ? figcaption.textContent : '';

			link.addEventListener('click', e => {
				e.preventDefault();

				const lightbox = GLightbox();
				lightbox.setElements([
					{
						href: link.href,
						type: 'image',
						title: caption
					}
				]);

				lightbox.open();
			});
		});
	}

	/**
	 * Initialize "load more news" button
	 */
	initShowMoreButtons() {
		const buttonWraps = document.querySelectorAll('.showMore')

		if (!buttonWraps.length) return

		buttonWraps.forEach(buttonWrap => {
			const button = document.getElementById('showMore__button')
			if (!button) return

			const appendToId = button.getAttribute('data-appendto')
			if (!appendToId) return

			const appendToEl = document.getElementById(appendToId)
			if (!appendToEl) return

			button.addEventListener('click', event => {
				const url = event.target.getAttribute('data-url')

				if (url == 'null') return

				buttonWrap.classList.add('showMore--loading')
				button.setAttribute('disabled', 'disabled')

				axios.get(url, {
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				}).then(function (response) {
					// If we got URL for next page, set it for button
					// Otherwise remove button wrapper
					if (response.data.nextPageURL) {
						button.setAttribute('data-url', response.data.nextPageURL)
					} else {
						buttonWrap.remove()
					}

					// Append articles
					appendToEl.innerHTML += response.data.itemsMarkup
				}).catch(function (error) {
					console.log(error);
				}).then(function () {
					buttonWrap.classList.remove('showMore--loading')
					button.removeAttribute('disabled')
				})
			})
		})
	}

	/**
 	* Initialize live search forms
 	*/
	initLiveSearch() {
		const elements = document.querySelectorAll('.liveSearch')

		if (!elements.length) return

		elements.forEach(element => {
			const form = element.querySelector('.liveSearch__form')
			if (!form) return

			const actionUrl = form.getAttribute('action')
			if (!actionUrl) return

			const method = form.getAttribute('method')
			if (!method) return

			const input = element.querySelector('.liveSearch__input')
			if (!input) return

			const appendToEl = element.querySelector('.liveSearch__results')
			if (!appendToEl) return

			// Prevent default submit
			element.addEventListener('submit', e => {
				e.preventDefault()
			})

			// Monitor search query changes
			let inputTimeout = null

			input.addEventListener('input', e => {
				clearTimeout(inputTimeout)

				let searchQuery = e.target.value

				inputTimeout = setTimeout(() => {
					element.classList.add('liveSearch--loading')

					axios({
						method: method,
						url: actionUrl,
						headers: { 'X-Requested-With': 'XMLHttpRequest' },
						params: {
							q: searchQuery
						}
					}).then(response => {
						appendToEl.innerHTML = response.data
					}).catch(error => {
						console.error(error)
					}).then(() => (
						element.classList.remove('liveSearch--loading')
					))
				}, 200)
			})

			// We might already have search query defined, so initially trigger search input
			// input.dispatchEvent(new Event('input', {
			// 	bubbles: true,
			// 	cancelable: false,
			// }))
		})
	}

	/**
 	* Initialize help nav links
 	*/
	initHelpNavLinks() {
		const searchLinks = document.querySelectorAll('.helpNav__link--search')

		searchLinks.forEach(item => {
			item.addEventListener('click', e => {
				e.preventDefault();
			})
		})
	}
}

window._site = new Site();
